import React from "react";
import { Link, useParams, useOutletContext, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { SOLUTIONS } from "@/data/content";
import { Reveal, StaggerGroup, StaggerChild } from "@/components/site/Reveal";

export default function SolutionDetail() {
    const { slug } = useParams();
    const { openDemo } = useOutletContext();
    const solution = SOLUTIONS.find((s) => s.slug === slug);

    if (!solution) return <Navigate to="/solutions" replace />;

    return (
        <div className="bg-warm">
            {/* HERO */}
            <section className="relative overflow-hidden pt-14 pb-12 bg-hero-glow">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="aurora-blob blob-violet drift-a" style={{ top: "-10%", left: "10%", width: 420, height: 420 }} />
                    <div className="aurora-blob blob-pink drift-b" style={{ top: "40%", right: "10%", width: 340, height: 340 }} />
                </div>
                <div className="container-x relative z-10">
                    <Reveal>
                        <Link
                            to="/solutions"
                            className="text-slate-500 hover:text-slate-900 text-sm inline-flex items-center gap-2 mb-8"
                        >
                            <i className="fa-solid fa-arrow-left" /> All solutions
                        </Link>
                    </Reveal>
                    <div className="grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <Reveal delay={0.05}>
                                <div className="text-sm text-slate-500 mb-4">{solution.eyebrow}</div>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <h1 className="heading-hero text-4xl sm:text-5xl lg:text-6xl">
                                    {solution.headline}
                                </h1>
                            </Reveal>
                            <Reveal delay={0.2}>
                                <p className="mt-6 text-intro text-slate-600 text-base md:text-lg max-w-2xl">
                                    {solution.summary}
                                </p>
                            </Reveal>
                            <Reveal delay={0.3}>
                                <div className="mt-8 flex flex-wrap gap-3">
                                    <button
                                        onClick={openDemo}
                                        className="pill-dark"
                                        data-testid={`solution-detail-book-demo-${solution.slug}`}
                                    >
                                        Book a demo
                                    </button>
                                    <Link to="/contact" className="pill-ghost">
                                        Talk to sales
                                    </Link>
                                </div>
                            </Reveal>
                        </div>

                        <Reveal delay={0.3} className="lg:col-span-5">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.96 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                whileHover={{ y: -4 }}
                                className="relative rounded-[1.75rem] overflow-hidden border border-white/50 group"
                                style={{ boxShadow: `0 30px 70px -30px ${solution.accent}66` }}
                            >
                                <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] w-full overflow-hidden">
                                    <img
                                        src={solution.image}
                                        alt={solution.title}
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    {/* accent tint + bottom gradient for legibility */}
                                    <div
                                        className="absolute inset-0 mix-blend-multiply opacity-25"
                                        style={{ background: `linear-gradient(160deg, ${solution.accent}, transparent 60%)` }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#0E2A23]/85 via-[#0E2A23]/10 to-transparent" />

                                    {/* floating icon chip */}
                                    <div
                                        className="absolute top-4 left-4 w-11 h-11 rounded-xl grid place-items-center text-white backdrop-blur-md"
                                        style={{ background: `${solution.accent}cc`, border: "1px solid rgba(255,255,255,0.35)" }}
                                    >
                                        <i className={`${solution.icon} text-base`} />
                                    </div>

                                    {/* label */}
                                    <div className="absolute bottom-0 left-0 right-0 p-6">
                                        <div className="text-[11px] uppercase tracking-[0.2em] text-white/75">
                                            {solution.eyebrow}
                                        </div>
                                        <div className="font-serif text-2xl md:text-3xl text-white mt-1 leading-tight">
                                            {solution.title}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* BUSINESS CHALLENGES */}
            {solution.businessChallenges && (
                <section className="py-20 bg-clean">
                    <div className="container-x">
                        <Reveal>
                            <div className="max-w-3xl mb-12">
                                <div className="text-sm text-slate-500 mb-3">The problem</div>
                                <h2 className="heading-section text-3xl md:text-5xl">
                                    Enterprise problems <span className="italic">{solution.title} solves</span>.
                                </h2>
                            </div>
                        </Reveal>
                        <StaggerGroup className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {solution.businessChallenges.map((c) => (
                                <StaggerChild key={c.title}>
                                    <div
                                        className="rounded-2xl p-5 h-full flex items-start gap-3 border border-slate-200 bg-white hover:border-slate-300 transition-all"
                                    >
                                        <div
                                            className="icon-sticker-soft w-9 h-9 shrink-0"
                                            style={{ "--st-color": "#dc2626" }}
                                        >
                                            <i className={`${c.icon} text-sm`} />
                                        </div>
                                        <div className="text-sm text-slate-800 leading-snug pt-1.5">
                                            {c.title}
                                        </div>
                                    </div>
                                </StaggerChild>
                            ))}
                        </StaggerGroup>
                    </div>
                </section>
            )}

            {/* THE SHIFT */}
            <section className="py-20 bg-warm">
                <div className="container-x grid md:grid-cols-12 gap-10">
                    <Reveal className="md:col-span-4">
                        <div className="text-sm text-slate-500 mb-3">The shift</div>
                        <h2 className="heading-section text-3xl md:text-4xl">
                            From reaction
                            <br />
                            <span className="text-slate-400">to augmentation.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="md:col-span-8">
                        <div className="gleap-card p-8 md:p-10">
                            <p className="text-slate-700 text-lg leading-relaxed">{solution.shift}</p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* CAPABILITIES */}
            <section className="py-20 bg-mint">
                <div className="container-x">
                    <Reveal>
                        <div className="text-sm text-slate-500 mb-3">Capabilities</div>
                        <h2 className="heading-section text-3xl md:text-5xl max-w-3xl">
                            What <span className="italic">{solution.title}</span> does.
                        </h2>
                    </Reveal>
                    <StaggerGroup className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {solution.capabilities.map((c) => (
                            <StaggerChild key={c}>
                                <motion.div
                                    whileHover={{ y: -2 }}
                                    transition={{ duration: 0.25 }}
                                    className="gleap-card p-6 h-full flex items-start gap-3"
                                >
                                    <i
                                        className="fa-solid fa-check text-sm mt-1 shrink-0"
                                        style={{ color: solution.accent }}
                                    />
                                    <div className="text-slate-700 text-sm">{c}</div>
                                </motion.div>
                            </StaggerChild>
                        ))}
                    </StaggerGroup>

                    {solution.channels && (
                        <Reveal delay={0.2} className="mt-12">
                            <div className="text-xs text-slate-500 uppercase tracking-[0.2em] mb-4">On every channel</div>
                            <div className="flex flex-wrap gap-2">
                                {solution.channels.map((ch) => (
                                    <div
                                        key={ch}
                                        className="glass-card px-4 py-1.5 text-sm text-slate-800"
                                        style={{ borderRadius: 999 }}
                                    >
                                        {ch}
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    )}
                </div>
            </section>

            {/* AI FEATURES */}
            {solution.aiFeatures && (
                <section className="py-20 bg-clean">
                    <div className="container-x">
                        <Reveal>
                            <div className="max-w-3xl mb-12">
                                <div className="text-sm text-slate-500 mb-3">AI features</div>
                                <h2 className="heading-section text-3xl md:text-5xl">
                                    <span className="italic">Intelligence</span> built in.
                                </h2>
                            </div>
                        </Reveal>
                        <StaggerGroup className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                            {solution.aiFeatures.map((f) => (
                                <StaggerChild key={f.label}>
                                    <motion.div
                                        whileHover={{ y: -3 }}
                                        transition={{ duration: 0.25 }}
                                        className="rounded-2xl p-5 h-full border border-slate-200 bg-white group hover:border-slate-300 transition-all"
                                    >
                                        <div
                                            className="icon-sticker w-11 h-11 mb-3"
                                            style={{ "--st-color": solution.accent }}
                                        >
                                            <i className={`${f.icon} text-base`} />
                                        </div>
                                        <div className="text-sm font-medium text-slate-900 leading-snug">
                                            {f.label}
                                        </div>
                                    </motion.div>
                                </StaggerChild>
                            ))}
                        </StaggerGroup>
                    </div>
                </section>
            )}

            {/* INGESTION SOURCES (Knowledge & TenderIQ) */}
            {solution.ingestionSources && (
                <section className="py-20 bg-cool">
                    <div className="container-x">
                        <Reveal>
                            <div className="max-w-3xl mb-6">
                                <div className="text-sm text-slate-500 mb-3">
                                    {solution.ingestionEyebrow || "Everything, connected"}
                                </div>
                                <h2
                                    className="heading-section text-3xl md:text-5xl"
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            solution.ingestionHeading ||
                                            "Ingests <em>from wherever</em> your knowledge lives.",
                                    }}
                                />
                            </div>
                        </Reveal>
                        {solution.knowledgeSources && (
                            <Reveal delay={0.1}>
                                <div className="mb-10 flex flex-wrap gap-2">
                                    {solution.knowledgeSources.map((k) => (
                                        <span
                                            key={k}
                                            className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600"
                                        >
                                            {k}
                                        </span>
                                    ))}
                                </div>
                            </Reveal>
                        )}
                        <StaggerGroup className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                            {solution.ingestionSources.map((s) => (
                                <StaggerChild key={s.name}>
                                    <div className="rounded-2xl p-4 h-full flex flex-col items-center text-center gap-2 border border-slate-200 bg-white hover:border-slate-300 hover:-translate-y-0.5 transition-all">
                                        <div
                                            className="icon-sticker w-10 h-10"
                                            style={{ "--st-color": solution.accent }}
                                        >
                                            <i className={`${s.icon} text-base`} />
                                        </div>
                                        <div className="text-xs text-slate-800 font-medium leading-tight">
                                            {s.name}
                                        </div>
                                    </div>
                                </StaggerChild>
                            ))}
                        </StaggerGroup>
                    </div>
                </section>
            )}

            {/* EVALUATION POINTS (TenderIQ) */}
            {solution.evaluationPoints && (
                <section className="py-20 bg-warm">
                    <div className="container-x">
                        <Reveal>
                            <div className="max-w-3xl mb-12">
                                <div className="text-sm text-slate-500 mb-3">What it extracts</div>
                                <h2 className="heading-section text-3xl md:text-5xl">
                                    Every criterion your evaluators <span className="italic">actually check</span>.
                                </h2>
                            </div>
                        </Reveal>
                        <StaggerGroup className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {solution.evaluationPoints.map((e) => (
                                <StaggerChild key={e.label}>
                                    <div className="rounded-2xl p-5 h-full flex items-start gap-3 border border-slate-200 bg-white hover:border-slate-300 transition-all">
                                        <div
                                            className="icon-sticker w-10 h-10 shrink-0"
                                            style={{ "--st-color": solution.accent }}
                                        >
                                            <i className={`${e.icon} text-sm`} />
                                        </div>
                                        <div className="text-sm font-medium text-slate-900 leading-snug pt-1.5">
                                            {e.label}
                                        </div>
                                    </div>
                                </StaggerChild>
                            ))}
                        </StaggerGroup>
                    </div>
                </section>
            )}

            {/* ARCHITECTURE FLOW */}
            {solution.architecture && (
                <section className="py-24 bg-warm">
                    <div className="container-x">
                        <Reveal>
                            <div className="max-w-3xl mb-14">
                                <div className="text-sm text-slate-500 mb-3">Architecture</div>
                                <h2 className="heading-section text-3xl md:text-5xl">
                                    {solution.architecture.title}
                                </h2>
                            </div>
                        </Reveal>

                        <Reveal delay={0.1}>
                            <div
                                className="rounded-3xl p-8 md:p-10 border border-slate-200"
                                style={{
                                    background:
                                        "linear-gradient(135deg, #ffffff 0%, #F5FAFF 100%)",
                                }}
                            >
                                <div className="flex flex-wrap items-center justify-center gap-3 md:gap-2">
                                    {solution.architecture.flow.map((node, i, arr) => (
                                        <React.Fragment key={node.label}>
                                            <motion.div
                                                whileHover={{ y: -3 }}
                                                className="rounded-2xl px-4 py-3 flex items-center gap-2.5 border bg-white shadow-sm"
                                                style={{ borderColor: `${solution.accent}30` }}
                                            >
                                                <div
                                                    className="icon-sticker w-8 h-8 shrink-0"
                                                    style={{ "--st-color": solution.accent }}
                                                >
                                                    <i className={`${node.icon} text-xs`} />
                                                </div>
                                                <div className="text-xs md:text-sm font-medium text-slate-800 max-w-[220px] leading-tight">
                                                    {node.label}
                                                </div>
                                            </motion.div>
                                            {i < arr.length - 1 && (
                                                <i
                                                    className="fa-solid fa-arrow-right text-slate-400 text-sm hidden md:inline"
                                                    aria-hidden
                                                />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </section>
            )}

            {/* INDUSTRIES */}
            {solution.industries && (
                <section className="py-20 bg-clean">
                    <div className="container-x">
                        <Reveal>
                            <div className="max-w-3xl mb-10">
                                <div className="text-sm text-slate-500 mb-3">Industries</div>
                                <h2 className="heading-section text-3xl md:text-5xl">
                                    Deployed across <span className="italic">regulated verticals</span>.
                                </h2>
                            </div>
                        </Reveal>
                        <StaggerGroup className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                            {solution.industries.map((ind) => (
                                <StaggerChild key={ind.name}>
                                    <div className="rounded-2xl p-5 h-full flex flex-col items-center text-center gap-3 border border-slate-200 bg-white hover:border-slate-900/40 transition-all">
                                        <div
                                            className="icon-sticker w-11 h-11"
                                            style={{ "--st-color": solution.accent }}
                                        >
                                            <i className={`${ind.icon} text-base`} />
                                        </div>
                                        <div className="text-xs md:text-sm text-slate-900 font-medium leading-tight">
                                            {ind.name}
                                        </div>
                                    </div>
                                </StaggerChild>
                            ))}
                        </StaggerGroup>
                    </div>
                </section>
            )}

            {/* GOVERNANCE + DEPLOYMENT */}
            {(solution.governance || solution.deployments) && (
                <section className="py-20 bg-mint">
                    <div className="container-x grid md:grid-cols-12 gap-8">
                        {solution.governance && (
                            <Reveal className="md:col-span-7">
                                <div className="text-sm text-slate-500 mb-3">Security & Governance</div>
                                <h2 className="heading-section text-3xl md:text-4xl mb-8">
                                    Enterprise-grade,<br />
                                    <span className="italic">by design.</span>
                                </h2>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {solution.governance.map((g) => (
                                        <div
                                            key={g.title}
                                            className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-slate-200"
                                        >
                                            <div
                                                className="icon-sticker w-9 h-9 shrink-0"
                                                style={{ "--st-color": solution.accent }}
                                            >
                                                <i className={`${g.icon} text-sm`} />
                                            </div>
                                            <div className="text-sm text-slate-800 leading-snug pt-1.5">
                                                {g.title}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Reveal>
                        )}
                        {solution.deployments && (
                            <Reveal delay={0.1} className="md:col-span-5">
                                <div className="text-sm text-slate-500 mb-3">Deployment</div>
                                <h2 className="heading-section text-3xl md:text-4xl mb-8">
                                    Runs <span className="italic">where you need it</span>.
                                </h2>
                                <div className="flex flex-col gap-3">
                                    {solution.deployments.map((d, i) => (
                                        <div
                                            key={d}
                                            className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-slate-200"
                                        >
                                            <div
                                                className="heading-stat text-3xl"
                                                style={{ color: solution.accent }}
                                            >
                                                {String(i + 1).padStart(2, "0")}
                                            </div>
                                            <div className="text-slate-900 font-medium">{d}</div>
                                        </div>
                                    ))}
                                </div>
                            </Reveal>
                        )}
                    </div>
                </section>
            )}

            {/* KEY DIFFERENTIATORS */}
            {solution.differentiators && (
                <section className="py-20 bg-clean">
                    <div className="container-x">
                        <Reveal>
                            <div className="max-w-3xl mb-12">
                                <div className="text-sm text-slate-500 mb-3">Why {solution.title}</div>
                                <h2 className="heading-section text-3xl md:text-5xl">
                                    What sets it <span className="italic">apart</span>.
                                </h2>
                            </div>
                        </Reveal>
                        <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {solution.differentiators.map((d, i) => (
                                <StaggerChild key={d}>
                                    <div className="h-full p-6 rounded-2xl border border-slate-200 bg-white flex gap-4 items-start">
                                        <div
                                            className="heading-stat text-4xl shrink-0"
                                            style={{ color: solution.accent }}
                                        >
                                            /{String(i + 1).padStart(2, "0")}
                                        </div>
                                        <div className="text-sm md:text-base text-slate-800 leading-relaxed pt-1">
                                            {d}
                                        </div>
                                    </div>
                                </StaggerChild>
                            ))}
                        </StaggerGroup>
                    </div>
                </section>
            )}

            {/* OUTCOMES */}
            <section className="py-20 bg-warm">
                <div className="container-x">
                    <Reveal>
                        <div className="text-sm text-slate-500 mb-3">Outcomes</div>
                        <h2 className="heading-section text-3xl md:text-5xl max-w-3xl">
                            What you <span className="italic">walk away with</span>.
                        </h2>
                    </Reveal>
                    <StaggerGroup className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {solution.outcomes.map((o, i) => (
                            <StaggerChild key={o}>
                                <div className="gleap-card p-6 flex items-center gap-5">
                                    <div
                                        className="heading-stat text-3xl shrink-0"
                                        style={{ color: solution.accent }}
                                    >
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <div className="text-slate-800">{o}</div>
                                </div>
                            </StaggerChild>
                        ))}
                    </StaggerGroup>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 bg-cta-glow">
                <div className="container-x text-center max-w-3xl mx-auto">
                    <Reveal>
                        <h2 className="heading-hero text-4xl sm:text-5xl md:text-6xl">
                            {solution.cta}.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                            <button
                                onClick={openDemo}
                                className="pill-dark"
                                data-testid={`solution-detail-footer-book-${solution.slug}`}
                            >
                                Book a demo
                            </button>
                            <Link to="/solutions" className="pill-ghost">
                                Explore other solutions
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
